import { useCallback, useEffect, useMemo, useState } from 'react';

interface SearchableItem {
  id: string;
  name: string;
}

interface UseHierarchicalSearchProps<T extends SearchableItem> {
  items: T[];
  searchText: string;
  getChildrenByParent: (parentId: string) => T[];
}

export const useHierarchicalSearch = <T extends SearchableItem>({
  items,
  searchText,
  getChildrenByParent,
}: UseHierarchicalSearchProps<T>) => {
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  const toggleItemExpansion = useCallback((itemId: string) => {
    setExpandedItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  }, []);

  const itemMatchesSearch = useCallback(
    (item: T): boolean => {
      if (!searchText.trim()) return true;
      return item.name.toLowerCase().includes(searchText.trim().toLowerCase());
    },
    [searchText],
  );

  // EWM-1135: parent lookup, built once instead of rediscovered per row.
  //
  // shouldShowItem is called from the table's per-row `rowStyle`, and it used to locate an item's
  // parent with `items.find(p => getChildrenByParent(p.id).some(c => c.id === item.id))` - a scan
  // over every item, for every row, on every render where the search box is non-empty. Measured at
  // 150 ms per render pass on a 4k-item estimation, i.e. per keystroke, and growing 3.4x per
  // doubling. Inverting the child lists into a Map once makes the same lookup O(1).
  const parentByChildId = useMemo(() => {
    const map = new Map<string, T>();
    for (const parent of items) {
      for (const child of getChildrenByParent(parent.id)) {
        map.set(child.id, parent);
      }
    }
    return map;
  }, [items, getChildrenByParent]);

  // Check if item should be visible (item matches OR has matching children OR parent matches)
  const shouldShowItem = useCallback(
    (item: T): boolean => {
      if (!searchText.trim()) return true;

      if (itemMatchesSearch(item)) return true;

      const children = getChildrenByParent(item.id);
      if (children.some((child) => itemMatchesSearch(child))) return true;

      const parent = parentByChildId.get(item.id);
      if (parent && itemMatchesSearch(parent)) return true;

      return false;
    },
    [searchText, itemMatchesSearch, getChildrenByParent, parentByChildId],
  );

  // Auto-expand parents with matching children
  useEffect(() => {
    if (searchText.trim()) {
      const itemsToExpand = new Set<string>();

      items.forEach((item) => {
        const children = getChildrenByParent(item.id);
        if (children.some((child) => itemMatchesSearch(child))) {
          itemsToExpand.add(item.id);
        }
      });

      setExpandedItems(itemsToExpand);
    }
  }, [searchText, items, getChildrenByParent, itemMatchesSearch]);

  return {
    expandedItems,
    toggleItemExpansion,
    shouldShowItem,
  };
};
